import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import type { SxProps } from "@mui/material/styles";
import type { muiTheme } from "@polifonia/theme";
import { theme } from "@polifonia/theme";
import { useTranslation } from "react-i18next";

import { Empty } from "@/components/Empty";
import { InfoBox } from "@/components/InfoBox";
import { MotifIcon } from "@/components/MotifIcon";
import { Tooltip } from "@/components/Tooltip";
import { Typography } from "@/components/Typography";
import type { InterludeTypes } from "@/interlude";
import { ApiKeyRow } from "@/pages/applications/pages/apiKeys/components/ApiKeyRow";
import { apiKeyTableStyles as styles } from "@/pages/applications/pages/apiKeys/components/apiKeyTableStyles";

type ApiKey = InterludeTypes["ApiKey"];
type SettableApiKeyStatus = InterludeTypes["SettableApiKeyStatus"];

interface ApiKeyTableProps {
  apiKeys: Array<ApiKey>;
  isLoading: boolean;
  isError: boolean;
  onRename: (apiKey: ApiKey) => void;
  onRetry: () => void;
  onRevoke: (apiKey: ApiKey) => void;
  onStatusUpdated: (status: SettableApiKeyStatus) => void;
}

const localStyles = {
  tableWrapper: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
  },
  emptyContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "12px",
    padding: "60px 0",
  },
  emptyIcon: {
    fontSize: "64px",
  },
  loadingContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "60px 0",
  },
  errorContainer: {
    padding: "24px 0",
  },
  infoIcon: {
    fontSize: "14px",
    color: theme.colors.staticFgPlaceholder,
    cursor: "help",
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

export const ApiKeyTable: React.FC<ApiKeyTableProps> = function ApiKeyTable({
  apiKeys,
  isLoading,
  isError,
  onRename,
  onRetry,
  onRevoke,
  onStatusUpdated,
}) {
  const { t } = useTranslation();

  return (
    <Box sx={localStyles.tableWrapper}>
      {/*
       * Columns: name, key, status, created.
       *
       * There is deliberately no "source" column telling a self-service key
       * from one CS issued. Interlude lists both kinds, but `ApiKeyOut`
       * exposes no field saying which is which, and the only thing that
       * correlates -- an empty `name` -- would mis-label the self-service keys
       * issued before naming existed. Pending a backend field.
       */}
      <Box sx={styles.row}>
        <Box sx={styles.colName}>
          <Typography
            variant="body"
            fontWeight={500}
            color={theme.colors.staticFgNote}
          >
            {t("apiKey.table.column.name", "Name")}
          </Typography>
        </Box>
        <Box sx={styles.colKey}>
          <Stack direction="row" alignItems="center" gap="4px">
            <Typography
              variant="body"
              fontWeight={500}
              color={theme.colors.staticFgNote}
            >
              {t("apiKey.table.column.key", "Key")}
            </Typography>
            <Tooltip
              title={t(
                "apiKey.table.keyTooltip",
                "Only the last few characters are shown. A key is displayed in full once, when it is created.",
              )}
              arrow
            >
              <Box component="span" sx={localStyles.infoIcon}>
                <MotifIcon un-i-motif="circle_info" />
              </Box>
            </Tooltip>
          </Stack>
        </Box>
        <Box sx={styles.colStatus}>
          <Typography
            variant="body"
            fontWeight={500}
            color={theme.colors.staticFgNote}
          >
            {t("apiKey.table.column.status", "Status")}
          </Typography>
        </Box>
        <Box sx={styles.colCreatedAt}>
          <Typography
            variant="body"
            fontWeight={500}
            color={theme.colors.staticFgNote}
          >
            {t("apiKey.table.column.createdAt", "Created")}
          </Typography>
        </Box>
        <Box sx={styles.colActions} />
      </Box>
      <Box sx={styles.divider} />

      {isLoading ? (
        <Box sx={localStyles.loadingContainer}>
          <CircularProgress size={32} />
        </Box>
      ) : null}

      {/*
       * A failed load says so instead of falling through to the empty state.
       * The list query's errors never reach `GlobalErrorHint` -- only
       * `MutationCache` emits to it -- so without this branch a 403 or an
       * unreachable gateway renders as "No API keys yet.", and the reader's
       * next move on a page like this one is to create a key they may already
       * have.
       */}
      {!isLoading && isError ? (
        <Box sx={localStyles.errorContainer} data-test="api-key-table-error">
          <InfoBox
            variant="error"
            title={t(
              "apiKey.table.error.title",
              "Could not load your API keys",
            )}
            description={t(
              "apiKey.table.error.description",
              "This list is not showing the keys this organization has. Try again before creating a key, so an existing one is not duplicated.",
            )}
            action={{
              label: t("apiKey.table.error.retry", "Try again"),
              // A retry the reader asked for, not a refresh after a mutation:
              // the zodios wrapper already invalidates this namespace when a
              // mutation settles, and AGENTS.md rules that case out.
              onClick: onRetry,
            }}
          />
        </Box>
      ) : null}

      {!isLoading && !isError && apiKeys.length === 0 ? (
        <Box sx={localStyles.emptyContainer} data-test="api-key-table-empty">
          <Box sx={localStyles.emptyIcon}>
            <Empty />
          </Box>
          <Typography variant="body" color={theme.colors.staticFgNote}>
            {t("apiKey.page.empty", "No API keys yet.")}
          </Typography>
        </Box>
      ) : null}

      {!isLoading
        ? apiKeys.map((apiKey) => (
            <ApiKeyRow
              key={apiKey.id}
              apiKey={apiKey}
              onRename={onRename}
              onRevoke={onRevoke}
              onStatusUpdated={onStatusUpdated}
            />
          ))
        : null}
    </Box>
  );
};
